import { CopilotChat } from '../CopilotChat';

import { Flex, HStack, Portal, Text } from '@chakra-ui/react';
import { CHAT_WIDTH } from '../BuyerOfferAnalysis/constants';

import { useEffect, useMemo, useRef } from 'react';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { IndigoLogoSmall } from '~/common/icons';
import { useBuyerOfferAnalysisContext } from '../BuyerOfferAnalysis/Context/BuyerOfferAnalysisContext';
import { MOBILE_FOOTER_HEIGHT } from './Mobile/MobileOfferScoreFooter';

export const OfferScoreCardChat = () => {
  const { isChatOpen, setIsChatOpen, headerHeight, isMobile } =
    useBuyerOfferAnalysisContext();

  const initialMessage = useMemo(() => {
    return [
      <InitialMessage key="offer-coach-initial-message" />,
      `Let's work on your offer together:
  1. Start filling out the offer terms
  2. I'll calculate the Offer Score and provide feedback as you go
  3. When complete, click "Share with buyer" to send them a view-only copy
  Let's get started!`,
    ];
  }, []);

  return (
    <Flex
      flexShrink={0}
      height={{ base: 'auto', xl: `calc(100dvh - ${headerHeight})` }}
      position={{ base: 'initial', xl: 'sticky' }}
      top={headerHeight}
      transform={{
        base: '',
        xl: isChatOpen ? 'translateX(0)' : 'translateX(100%)',
      }}
      transition="all 0.3s"
      width={{ base: 0, xl: isChatOpen ? CHAT_WIDTH : '0px' }}
    >
      <ChatContainer>
        <CopilotChat
          autoFocus={!isMobile && isChatOpen}
          darkerStyle={isMobile}
          labels={{
            initial: initialMessage,
          }}
          title="Offer Coach"
          onClose={() => setIsChatOpen(false)}
        />
      </ChatContainer>
    </Flex>
  );
};

const InitialMessage = () => {
  return (
    <HStack key="offer-coach-logo" mb={4}>
      <Flex
        alignItems="center"
        bg="purpleGradient"
        borderRadius="full"
        height="24px"
        justifyContent="center"
        p={4}
        width="24px"
      >
        <IndigoLogoSmall color="white" />
      </Flex>
      <Text fontSize="sm">Welcome to your Offer Scorecard</Text>
    </HStack>
  );
};

export const ChatContainer = ({ children }: { children: React.ReactNode }) => {
  const { isChatOpen, headerHeight } = useBuyerOfferAnalysisContext();
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onTouchMove = (e: TouchEvent) => {
      const target = e.target as HTMLElement;

      if (isChatOpen && !target.closest('.copilotKitMessages')) {
        e.preventDefault();
      }
    };

    document.body.addEventListener('touchmove', onTouchMove, {
      passive: false,
    });

    return () => {
      document.body.removeEventListener('touchmove', onTouchMove);
    };
  }, [isMobile, isChatOpen]);

  useEffect(() => {
    const onResize = () => {
      if (containerRef.current) {
        const height =
          (window.visualViewport?.height ?? window.innerHeight) -
          MOBILE_FOOTER_HEIGHT;
        containerRef.current.style.height = `${height}px`;
      }

      setTimeout(() => {
        const copilotFooter = document.querySelector<HTMLDivElement>(
          '.copilotKitMessages footer'
        );

        if (copilotFooter) {
          copilotFooter.scrollIntoView();
        }
      }, 300);
    };
    window.visualViewport?.addEventListener('resize', onResize);

    return () => {
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, []);

  const height = window.visualViewport?.height ?? window.innerHeight;

  return (
    <Portal>
      <Flex
        ref={containerRef}
        autoFocus={true}
        borderTop={{ base: '1px solid', xl: 'none' }}
        borderTopColor="whiteAlpha.200"
        height={{
          base: `calc(${height}px - ${MOBILE_FOOTER_HEIGHT}px)`,
          xl: `calc(100dvh - ${headerHeight})`,
        }}
        left={{ base: '0', xl: 'initial' }}
        overflow="hidden"
        position="fixed"
        right={{ base: '0', xl: isChatOpen ? '0' : '-100%' }}
        style={{
          scrollbarWidth: 'thin',
        }}
        top={{ base: isChatOpen ? '0' : '100%', xl: headerHeight }}
        transition="right 0.3s, bottom 0.3s, top 0.3s"
        width={{ base: '100%', xl: `calc(${CHAT_WIDTH} + 10px)` }}
        zIndex={5}
      >
        {children}
      </Flex>
    </Portal>
  );
};
